import { useEffect, useState } from 'react';
import { Client } from 'xrpl';

export interface HeartbeatStatus {
  latency: number | null;
  lastPing: Date | null;
  isHealthy: boolean;
}

interface UseHeartbeatOptions {
  interval?: number; // milliseconds between pings (default: 15000 = 15s)
  timeout?: number; // max acceptable latency in ms (default: 1000)
  enabled?: boolean; // whether heartbeat is enabled (default: true)
}

/**
 * Hook to monitor XRPL connection health via periodic ping requests
 * Measures round-trip latency and tracks connection health
 */
export const useHeartbeat = (
  client: Client | null,
  isConnected: boolean | undefined,
  options: UseHeartbeatOptions = {}
) => {
  const {
    interval = 15000, // ping every 15 seconds
    timeout = 1000, // consider unhealthy if > 1 second
    enabled = true,
  } = options;

  const [heartbeat, setHeartbeat] = useState<HeartbeatStatus>({
    latency: null,
    lastPing: null,
    isHealthy: true,
  });

  useEffect(() => {
    if (!client || !isConnected || !enabled) {
      return;
    }

    const sendPing = async () => {
      try {
        const startTime = performance.now();

        await client.request({
          command: 'ping',
        });

        const endTime = performance.now();
        const latency = Math.round(endTime - startTime);

        setHeartbeat({
          latency,
          lastPing: new Date(),
          isHealthy: latency < timeout,
        });
      } catch (error) {
        console.error('[heartbeat] ping failed:', error);
        setHeartbeat({
          latency: null,
          lastPing: new Date(),
          isHealthy: false,
        });
      }
    };

    // Send initial ping
    sendPing();

    // Set up interval for periodic pings
    const intervalId = setInterval(sendPing, interval);

    return () => {
      clearInterval(intervalId);
    };
  }, [client, isConnected, enabled, interval, timeout]);

  return heartbeat;
};
